import { Pog } from "@CommonTypes/merchflow/pog/pog";
import { KeyCode } from "src/utils/keys";
import * as THREE from "three";
import {
  PLANOGRAM_FRONT_MAX_ZOOM,
  PLANOGRAM_FRONT_MIN_ZOOM,
  PLANOGRAM_SCALE,
  PLANOGRAM_FRONT_ZOOM_SPEED,
} from "../constants";

const fitPogToCamera = (container: HTMLDivElement, pog: Pog, camera: THREE.OrthographicCamera) => {
  const planogramHeight = pog.planogram.getHeight() * PLANOGRAM_SCALE;
  const planogramHalfHeight = planogramHeight / 2;
  const containerRatio = container.clientWidth / container.clientHeight;
  const planogramHalfWidth = planogramHalfHeight * containerRatio;

  camera.top = planogramHalfHeight;
  camera.bottom = -planogramHalfHeight;
  camera.left = -planogramHalfWidth;
  camera.right = planogramHalfWidth;

  camera.position.x = planogramHalfWidth;
  camera.position.y = planogramHalfHeight;

  camera.userData.fittedFrustumHeight = planogramHalfHeight;
  camera.userData.fittedFrustumWidth = planogramHalfWidth;
  camera.userData.fittedX = camera.position.x;
  camera.userData.fittedY = camera.position.y;

  camera.updateProjectionMatrix();
};

export const createFrontCamera = (container: HTMLDivElement, pog: Pog) => {
  const camera = new THREE.OrthographicCamera(0, 0, 0, 0, 1, 5000);
  camera.position.set(0, 0, 500);
  camera.lookAt(0, 0, 0);

  fitPogToCamera(container, pog, camera);

  return { camera, cleanup: enableFrontPanning(camera, container) };
};

export const enableFrontPanning = (camera: THREE.OrthographicCamera, container: HTMLDivElement) => {
  let isDragging = false;
  let lastX = 0;
  let lastY = 0;

  const onMouseDown = (event: MouseEvent) => {
    isDragging = true;
    lastX = event.clientX;
    lastY = event.clientY;
    container.style.cursor = "grabbing";
  };

  const onMouseUp = () => {
    isDragging = false;
    container.style.cursor = "default";
  };

  const onMouseMove = (event: MouseEvent) => {
    if (!isDragging) return;

    const deltaX = event.clientX - lastX;
    const deltaY = event.clientY - lastY;
    lastX = event.clientX;
    lastY = event.clientY;

    const worldDX = (deltaX * (camera.right - camera.left)) / (container.clientWidth * camera.zoom);

    const worldDY =
      (deltaY * (camera.top - camera.bottom)) / (container.clientHeight * camera.zoom);

    camera.position.x -= worldDX;
    camera.position.y += worldDY;
  };

  const onWheel = (event: WheelEvent) => {
    event.preventDefault();

    const bounds = container.getBoundingClientRect();

    // Cursor position in Normalized Device Coordinates (-1 to +1)
    const cursorNormalizedCoordinates = new THREE.Vector2(
      ((event.clientX - bounds.left) / bounds.width) * 2 - 1,
      -((event.clientY - bounds.top) / bounds.height) * 2 + 1,
    );

    // World position under cursor BEFORE zoom
    const worldBeforeZoom = new THREE.Vector3(
      cursorNormalizedCoordinates.x,
      cursorNormalizedCoordinates.y,
      0,
    ).unproject(camera);

    const zoomFactor = 1 - event.deltaY * PLANOGRAM_FRONT_ZOOM_SPEED;
    const nextZoom = THREE.MathUtils.clamp(
      camera.zoom * zoomFactor,
      PLANOGRAM_FRONT_MIN_ZOOM,
      PLANOGRAM_FRONT_MAX_ZOOM,
    );

    camera.zoom = nextZoom;
    camera.updateProjectionMatrix();

    // World position under cursor AFTER zoom
    const worldAfterZoom = new THREE.Vector3(
      cursorNormalizedCoordinates.x,
      cursorNormalizedCoordinates.y,
      0,
    ).unproject(camera);

    // Shift camera so the world point under the cursor stays fixed
    camera.position.x += worldBeforeZoom.x - worldAfterZoom.x;
    camera.position.y += worldBeforeZoom.y - worldAfterZoom.y;
  };

  const onResize = () => {
    const width = container.clientWidth;
    const height = container.clientHeight;

    const aspect = width / height;
    const frustumHeight = camera.userData.fittedFrustumHeight as number;
    const frustumWidth = frustumHeight * aspect;

    camera.left = -frustumWidth;
    camera.right = frustumWidth;
    camera.top = frustumHeight;
    camera.bottom = -frustumHeight;

    // Adjust position to match new frustum
    camera.position.x = camera.userData.fittedX - camera.userData.fittedFrustumWidth + frustumWidth;
    camera.position.y =
      camera.userData.fittedY - camera.userData.fittedFrustumHeight + frustumHeight;

    camera.updateProjectionMatrix();
  };

  container.addEventListener("mousedown", onMouseDown);
  container.addEventListener("mousemove", onMouseMove);
  container.addEventListener("mouseup", onMouseUp);
  container.addEventListener("wheel", onWheel);
  window.addEventListener("resize", onResize);

  return () => {
    container.removeEventListener("mousedown", onMouseDown);
    container.removeEventListener("mousemove", onMouseMove);
    container.removeEventListener("mouseup", onMouseUp);
    container.removeEventListener("wheel", onWheel);
    window.removeEventListener("resize", onResize);
  };
};

export const create3dCamera = (
  renderer: THREE.WebGLRenderer,
  container: HTMLDivElement,
  pog: Pog,
) => {
  const camera = new THREE.PerspectiveCamera(
    75,
    container.clientWidth / container.clientHeight,
    1,
    50000,
  );

  camera.position.set(
    (pog.planogram.getWidth() / 2) * PLANOGRAM_SCALE,
    (pog.planogram.getHeight() / 2) * PLANOGRAM_SCALE,
    300,
  );
  camera.lookAt((pog.planogram.getWidth() / 2) * PLANOGRAM_SCALE, 0, -300);

  const controls = enableFPSControls(renderer, camera, container);

  return {
    camera,
    cleanup: controls.cleanup,
    updateMovement: controls.updateMovement,
  };
};

export const enableFPSControls = (
  renderer: THREE.WebGLRenderer,
  camera: THREE.PerspectiveCamera,
  container: HTMLDivElement,
) => {
  const move = { forward: false, backward: false, left: false, right: false };
  const timer = new THREE.Timer();
  let isMouseDown = false;
  const mousePos = { x: 0, y: 0 };
  const sensitivity = 0.002;
  const speed = 300;

  const onKeyDown = (event: KeyboardEvent) => {
    if (event.code === KeyCode.W) move.forward = true;
    if (event.code === KeyCode.S) move.backward = true;
    if (event.code === KeyCode.A) move.left = true;
    if (event.code === KeyCode.D) move.right = true;
  };

  const onKeyUp = (event: KeyboardEvent) => {
    if (event.code === KeyCode.W) move.forward = false;
    if (event.code === KeyCode.S) move.backward = false;
    if (event.code === KeyCode.A) move.left = false;
    if (event.code === KeyCode.D) move.right = false;
  };

  const onMouseDown = (event: MouseEvent) => {
    isMouseDown = true;
    mousePos.x = event.clientX;
    mousePos.y = event.clientY;
    container.style.cursor = "grabbing";
  };

  const onMouseUp = () => {
    isMouseDown = false;
    container.style.cursor = "default";
  };

  const onMouseMove = (event: MouseEvent) => {
    if (!isMouseDown) return;

    const deltaX = event.clientX - mousePos.x;
    const deltaY = event.clientY - mousePos.y;
    mousePos.x = event.clientX;
    mousePos.y = event.clientY;

    camera.rotation.y -= deltaX * sensitivity;
    camera.rotation.x -= deltaY * sensitivity;
    camera.rotation.x = Math.max(-Math.PI / 2, Math.min(Math.PI / 2, camera.rotation.x));
  };

  const onResize = () => {
    const width = container.clientWidth;
    const height = container.clientHeight;

    camera.aspect = width / height;
    camera.updateProjectionMatrix();
  };

  const onWheel = (event: WheelEvent) => {
    event.preventDefault();

    const bounds = container.getBoundingClientRect();

    // Cursor position in Normalized Device Coordinates (-1 to +1)
    const cursorNormalizedCoordinates = new THREE.Vector2(
      ((event.clientX - bounds.left) / bounds.width) * 2 - 1,
      -((event.clientY - bounds.top) / bounds.height) * 2 + 1,
    );

    // Raycast from camera through cursor
    const raycaster = new THREE.Raycaster();
    raycaster.setFromCamera(cursorNormalizedCoordinates, camera);

    // Assume we want to move along camera's forward direction
    const zoomDistance = event.deltaY * -0.5;

    const forward = new THREE.Vector3();
    camera.getWorldDirection(forward);

    camera.position.add(forward.multiplyScalar(zoomDistance));
  };

  document.addEventListener("keydown", onKeyDown);
  document.addEventListener("keyup", onKeyUp);
  container.addEventListener("mousedown", onMouseDown);
  container.addEventListener("mouseup", onMouseUp);
  container.addEventListener("mousemove", onMouseMove);
  container.addEventListener("wheel", onWheel);
  window.addEventListener("resize", onResize);

  const updateMovement = () => {
    timer.update();
    const delta = timer.getDelta();

    const forward = new THREE.Vector3();
    camera.getWorldDirection(forward);

    const right = new THREE.Vector3();
    right.crossVectors(forward, camera.up).normalize();

    const velocity = new THREE.Vector3();
    if (move.forward) velocity.add(forward);
    if (move.backward) velocity.add(forward.clone().negate());
    if (move.left) velocity.add(right.clone().negate());
    if (move.right) velocity.add(right);

    velocity.multiplyScalar(speed * delta);
    camera.position.add(velocity);
  };

  const cleanup = () => {
    document.removeEventListener("keydown", onKeyDown);
    document.removeEventListener("keyup", onKeyUp);
    container.removeEventListener("mousedown", onMouseDown);
    container.removeEventListener("mouseup", onMouseUp);
    container.removeEventListener("mousemove", onMouseMove);
    container.removeEventListener("wheel", onWheel);
    window.removeEventListener("resize", onResize);
  };

  return { updateMovement, cleanup };
};
